import { useContext, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Button } from "@blueprintjs/core";
import { faSlack, faTwitter } from "@fortawesome/free-brands-svg-icons";
import {
  faChevronsLeft,
  faChevronsRight,
  faEnvelope,
  faGraduationCap,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Text, Title } from "@mantine/core";
import { useAsyncEffect } from "@react-hook/async";

import { Beta, BrandIcon, Link, PurpleHighlightTagButton } from "@components";
import { useFindingCounts, useOrg } from "@hooks";
import { UserContext } from "@providers";
import { Permission } from "@types";
import {
  abbreviateNumber,
  Feature,
  SEMGREP_REPO,
  SLACK_INVITE_URL,
  TWITTER_URL,
} from "@utils";

import { MailingListBox } from "@pages/appLanding/components";
import { Changelog } from "@pages/dashboard/components/Changelog";
import { FooterIcon } from "@pages/dashboard/components/FooterIcon";
import { OrgPicker } from "@pages/dashboard/components/OrgPicker";
import { ScanStatusIndicator } from "@pages/dashboard/components/ScanStatusIndicator";
import { slugifyOrgName } from "@pages/dashboard/utils";

import { fetchLatestReleaseInfo } from "../../api";

interface DashboardContainerProps {
  isCollapsed: boolean;
}

export const NavSidebarContainer = styled.nav<DashboardContainerProps>`
  font-weight: var(--global-font-weight-regular);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  background: var(--indigo-gradient-180);
  color: var(--global-light-gray);
  padding: 12px;
  padding-bottom: 0px;
  width: ${(props) => (props.isCollapsed ? "24px" : "200px")};
  transition: 0.2s ease-out;
`;

const TabSection = styled.div`
  overflow: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const LogoSection = styled.div`
  text-align: center;
  padding-top: var(--global-spacing-l);
  font-size: var(--global-font-size-xs);
  overflow: hidden; // hide when sidebar collapses
  white-space: nowrap;
`;

const LogoLinks = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--global-spacing-m);
  padding: var(--global-spacing-m) var(--global-spacing-s);
  border-top: 1px solid var(--global-blue-gray);
`;

const FooterExt = styled.a`
  padding-right: 2px;
  color: white;
  &:hover {
    color: white;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;

  & > :not(:first-child) {
    margin-top: var(--global-spacing-s);
  }
`;

const DocLink = styled.a`
  &,
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    color: inherit;
    text-decoration: inherit;
  }
`;

const OrgPickerFlex = styled.div<DashboardContainerProps>`
  display: flex;
  justify-content: space-between;
  gap: 4px;
  direction: rtl; // so the button shows when the container collapses
  margin: ${(props) => (props.isCollapsed ? "0 -8px" : "0")};
  transition: 0.2s ease-out;
`;

const OrgPickerContainer = styled.div`
  min-width: 0;
  flex-grow: 1;
  direction: ltr; // undo direction from parent
`;

const CollapseButton = styled(Button)`
  padding: 0;
  background: #3a115c !important;
  border: 1px solid #644980;
  height: 42px;
  min-width: 24px;
  box-shadow: none !important;
  position: relative;

  &:hover {
    background: #543472 !important;
  }

  &:active {
    background: #593b77 !important;
  }
`;

const NavHeading = styled.h3`
  font-size: var(--global-font-size-xs);
  color: var(--global-light-gray);
  margin: 0 0 0 var(--global-spacing-m);
`;

interface NavItemProps {
  isActive?: boolean;
}

const NavItem = styled.li<NavItemProps>`
  font-size: var(--global-font-size-s);
  padding: 0 var(--global-spacing-m);
  border-radius: 2px;
  background: ${(props) => (props.isActive ? "rgba(21, 1, 36, 0.75)" : "none")};
  color: var(--global-light-gray);
  display: flex;
  white-space: nowrap; // elements won't wrap while sidebar collapses

  &:hover {
    background: ${(props) =>
      props.isActive ? "rgba(21, 1, 36, 0.75)" : "rgba(21, 1, 36, 0.25)"};
  }
`;

const NavIndicator = styled.div`
  margin-left: auto;
`;

const NavLink: React.FC<{
  indented?: boolean;
  exact?: boolean;
  name: string;
  path: string;
  notifications?: number;
  beta?: boolean;
  indicator?: React.ReactNode;
}> = ({
  exact = false,
  indented = false,
  name,
  path,
  beta = false,
  notifications = 0,
  indicator,
}) => {
  const location = useLocation();
  const currPath = location.pathname;
  return (
    <Link
      style={{ marginLeft: indented ? "8px" : "0px" }}
      // Pass search forward (simple way) to maintain
      // it between different navigations. Can be made
      // more refined, when needed (allowlist q params)
      to={{ pathname: path }}
    >
      <NavItem
        isActive={exact ? currPath === path : currPath.startsWith(path)}
        data-testid={`nav-${name.toLowerCase()}`}
      >
        <span>{beta ? <Beta>{name}</Beta> : name}</span>
        <span data-testid="sidebar-notification" style={{ marginLeft: "4px" }}>
          {notifications > 0 && ` (${abbreviateNumber(notifications)})`}
        </span>
        <NavIndicator>{indicator}</NavIndicator>
      </NavItem>
    </Link>
  );
};

export const NavSidebar = ({ permissions }: { permissions: Permission[] }) => {
  const { user } = useContext(UserContext);
  const org = useOrg();
  const basePath = `/orgs/${slugifyOrgName(org?.name ?? "-")}`;
  const [tagName, setTagName] = useState<string>("");
  const history = useHistory();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { data: findingCounts } = useFindingCounts();

  useAsyncEffect(async () => {
    fetchLatestReleaseInfo("returntocorp", "semgrep").then((releaseInfo) => {
      setTagName(releaseInfo.tag_name);
    });
  }, []);

  return (
    <NavSidebarContainer isCollapsed={isCollapsed}>
      <div>
        <OrgPickerFlex isCollapsed={isCollapsed}>
          <CollapseButton
            intent="primary"
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={
              <FontAwesomeIcon
                icon={isCollapsed ? faChevronsRight : faChevronsLeft}
                size={"sm"}
              />
            }
          />
          <OrgPickerContainer>
            <OrgPicker hidden={isCollapsed} />
          </OrgPickerContainer>
        </OrgPickerFlex>
        <TabSection>
          <NavLinks>
            <NavHeading>
              {user && !Feature.hasTeamsTier({ org }) ? (
                <PurpleHighlightTagButton
                  onClick={() => history.push(`${basePath}/settings/payment`)}
                  text="Upgrade"
                />
              ) : (
                <div />
              )}
            </NavHeading>
            {permissions.includes(Permission.panel_overview) && (
              <NavLink name="Dashboard" path={basePath} exact />
            )}
            {permissions.includes(Permission.panel_projects) && (
              <NavLink
                name="Projects"
                path={`${basePath}/projects`}
                indicator={<ScanStatusIndicator />}
              />
            )}
            {permissions.includes(Permission.panel_policy) &&
              (Feature.isNolicy({ org }) ? (
                <NavLink name="Rule board" path={`${basePath}/board`} />
              ) : (
                <NavLink name="Policies" path={`${basePath}/policies`} />
              ))}
            {permissions.includes(Permission.panel_findings) && (
              <NavLink
                name="Findings"
                path={`${basePath}/findings`}
                notifications={findingCounts?.open}
              />
            )}
            {Feature.hasSCA({ org }) &&
              permissions.includes(Permission.panel_findings) && (
                <NavLink
                  name="Dependencies"
                  path={`${basePath}/dependencies`}
                />
              )}
            <NavLink name="Editor" path={`${basePath}/editor`} />
            {permissions.includes(Permission.panel_settings) && (
              <NavLink name="Settings" path={`${basePath}/settings`} />
            )}
            <NavLink name="Support" path={`${basePath}/support`} />
            <br />
            <NavLink name="Registry" path={"/r"} />
            <NavLink name="Playground" path={"/playground"} />
            <DocLink href={"/docs"}>
              <NavItem>Docs</NavItem>
            </DocLink>
            {user?.roles?.admin === true && (
              <NavLink name="Admin" path={"/admin"} />
            )}
          </NavLinks>
        </TabSection>
      </div>
      <LogoSection>
        <BrandIcon style={{ height: "28px", width: "72px" }} />
        <div>
          <FooterExt href={SEMGREP_REPO}>
            Semgrep {(tagName || "").replace("v", "")}
          </FooterExt>
        </div>
        <LogoLinks>
          <FooterIcon
            icon={faGraduationCap}
            path="/learn"
            text="Learn Semgrep"
          />
          <FooterIcon
            icon={faEnvelope}
            popover={
              <Stack>
                <Title order={3} size="h5" weight="normal">
                  Semgrep Newsletter
                </Title>
                <Text size="sm">Join our newsletter to stay up to date.</Text>
                <MailingListBox />
              </Stack>
            }
          />
          <FooterIcon
            icon={faSlack}
            path={SLACK_INVITE_URL}
            text="Slack Community"
          />
          <FooterIcon
            icon={faTwitter}
            path={TWITTER_URL}
            text="Follow on Twitter"
          />
          <Changelog />
        </LogoLinks>
      </LogoSection>
    </NavSidebarContainer>
  );
};
